import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Moon,
  Sparkles,
  Loader2,
  Star,
  Cloud,
  Heart,
  Mic,
  ChevronLeft,
  ChevronRight,
  Trash2,
  TrendingUp,
  BookOpen,
  X,
} from "lucide-react";

import {
  analyzeDream,
  saveDream,
  listMyDreams,
  updateDreamReflection,
  deleteDream,
  getDreamPatterns,
  type DreamAnalysis,
  type DreamRow,
  type DreamPatterns,
} from "@/lib/dreams.functions";
import { detectCrisis } from "@/lib/crisis-detection";
import { reportCrisisIfDetected } from "@/lib/crisis-report";
import { CrisisDialog } from "@/components/CrisisDialog";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { FavoriteButton } from "@/components/FavoriteButton";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { PrivacyNotice } from "@/components/PrivacyNotice";
import { SwipeRail } from "@/components/SwipeRail";

import { notifyActivityChange } from "@/lib/activity-events";
import { readStoredAuthSession } from "@/lib/auth-session";
import { JournalGuide } from "@/components/JournalGuide";
import { useScrollTopOnChange } from "@/hooks/use-scroll-top-on-change";

import { AiConsentDialog, hasAiConsent } from "@/components/AiConsentDialog";

export const Route = createFileRoute("/dreams")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      {
        title: "Dream Journal | Record & Reflect on Dreams",
      },
      {
        name: "description",
        content:
          "A gentle, trauma-informed dream journal. Record dreams by voice or writing, reflect with 3-4 soft prompts, and notice recurring people, places, emotions and symbols over time.",
      },
      { property: "og:title", content: "Dream Journal — Recovery Trauma™" },
      {
        property: "og:description",
        content: "Record. Reflect. Notice patterns. A trauma-informed dream journal.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/dreams" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/dreams" }],
  }),
  component: withMemberGate(DreamsPage, "Dream Journal"),
});

const MOODS = ["Peaceful", "Heavy", "Hopeful", "Anxious", "Curious", "Tender"];

function createGuestDreamAnalysis(dream: string, mood?: string): DreamAnalysis {
  const words = dream
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 4)
    .slice(0, 5);

  return {
    suggestedTitle: mood ? `${mood} dream` : "Dream reflection",
    tags: {
      people: [],
      places: [],
      emotions: mood ? [mood.toLowerCase()] : [],
      symbols: words.slice(0, 3),
      themes: [],
    },
    summary: "You shared a dream worth sitting with.",
    emotionAcknowledgement: mood
      ? `You noted feeling ${mood.toLowerCase()} on waking.`
      : "You're welcome to notice what feelings, if any, surface as you sit with this.",
    gentlePossibilities: [
      "Some people find that returning to a dream gently — without rushing to interpret — lets new noticings surface in their own time.",
    ],
    reflectionQuestions: [
      "What feeling stayed with you after waking?",
      "Was there any image, place, or person that felt important?",
      "What might feel supportive to give yourself today?",
    ],
    closingReminder:
      "There is no single correct meaning for a dream. Your own experiences and reflections matter most.",
  };
}


type View =
  | { kind: "home" }
  | { kind: "compose"; mode: "write" | "voice"; category?: string }
  | { kind: "reflect"; dream: DreamRow; analysis: DreamAnalysis }
  | { kind: "dream"; dream: DreamRow };

const DREAM_CATEGORIES: { id: string; label: string; emoji: string; hint: string }[] = [
  { id: "Dream Entry", label: "Dream", emoji: "🌙", hint: "A dream you'd like to remember" },
  { id: "Nightmare", label: "Nightmare", emoji: "🌫️", hint: "Something difficult or distressing" },
  { id: "Recurring Dream", label: "Recurring Dream", emoji: "🔄", hint: "A dream or pattern that keeps returning" },
  { id: "Lucid Dream", label: "Lucid Dream", emoji: "✨", hint: "A dream where you knew you were dreaming" },
];

const DREAM_SUPPORT_LINKS: { emoji: string; label: string; dest: string; to: string }[] = [
  { emoji: "🌿", label: "Ground after a difficult dream", dest: "Grounding", to: "/grounding" },
  { emoji: "🌬️", label: "Settle my body", dest: "Breathing", to: "/breathing" },
  { emoji: "🎧", label: "Listen to something calming", dest: "Sounds", to: "/sounds" },
  { emoji: "🛏️", label: "Help me return to sleep", dest: "Sleep & Rest", to: "/sleep-dreams" },
  { emoji: "📚", label: "Understand dreams", dest: "Library → Dreams & Sleep", to: "/library" },
];

/* The gentle reflection prompts offered after a dream is saved. The AI
 * reflection (if the member asked for one) can add its own questions —
 * these are the always-present, member-led ones. */
const GENTLE_DREAM_QUESTIONS = [
  "What emotions stood out in the dream?",
  "What part of the dream feels most important to you?",
  "Were there any people, places, objects or symbols that stood out?",
  "Does anything in the dream connect with something happening in your life right now?",
  "Did the dream remind you of a past experience, relationship or feeling?",
  "Was there a moment where you felt safe, unsafe, trapped, curious, powerful or helpless?",
  "If this dream had a message for you, what might it be?",
  "Is there anything you want to remember or take away from this dream?",
];


const EMPTY_DREAM_PATTERNS: DreamPatterns = {
  totals: { dreams: 0 },
  people: [],
  places: [],
  emotions: [],
  symbols: [],
  themes: [],
  insights: [],
};

// Read the auth session synchronously from localStorage. We deliberately
// avoid `supabase.auth.getSession()` here — on mobile Safari/Chrome that
// call can hang for many seconds when the tab has been backgrounded,
// blocking the save request from ever leaving the device.
function getStoredSession() {
  return readStoredAuthSession();
}

function ensureFreshSession() {
  const session = readStoredAuthSession();
  if (!session?.user?.id) {
    throw new Error("Please sign in again to save your dream.");
  }
}

/* ───────────── Composer draft safety net ─────────────
 * A dream is often the most fragile thing a member types: it fades fast and
 * cannot be retyped. We persist the in-progress composer locally on every
 * keystroke and clear it ONLY after the cloud save is confirmed. */
const DREAM_DRAFT_KEY = "rt.dream.composer.draft.v1";

type DreamDraft = { dream: string; mood: string; title: string };

function readDreamDraft(): DreamDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(DREAM_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DreamDraft>;
    return {
      dream: typeof parsed.dream === "string" ? parsed.dream : "",
      mood: typeof parsed.mood === "string" ? parsed.mood : "",
      title: typeof parsed.title === "string" ? parsed.title : "",
    };
  } catch {
    return null;
  }
}

function writeDreamDraft(draft: DreamDraft) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DREAM_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* storage full or blocked — the in-memory composer still holds the text */
  }
}

function clearDreamDraft() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DREAM_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}


const DREAM_LENSES: { title: string; emoji: string; body: string; more: string[] }[] = [
  {
    title: "Freud",
    emoji: "📜",
    body: "Freud viewed dreams as the 'royal road to the unconscious' — wishes, fears and unfinished feelings finding symbolic expression while we sleep.",
    more: [
      "For Freud, dreams were a doorway into the parts of the mind we don't usually let ourselves see in daylight — desires, conflicts and old hurts that the waking mind quietly sets aside.",
      "He believed every dream had a 'manifest' surface (what we remember) and a 'latent' meaning underneath (what it might be pointing toward). Symbols, he thought, were the mind's gentle disguise for feelings that felt too tender to name directly.",
      "Modern psychology has moved on from many of Freud's specific ideas, yet the deeper invitation still feels relevant: dreams can be a way of meeting feelings we haven't yet had space to feel.",
    ],
  },
  {
    title: "Jung",
    emoji: "🌒",
    body: "Jung saw dreams as messengers from the deeper self — images, archetypes and inner figures inviting wholeness rather than diagnosis.",
    more: [
      "Jung believed dreams weren't trying to hide anything from us — they were trying to show us something we hadn't yet recognised in ourselves.",
      "He spoke of archetypes: universal patterns like the Child, the Shadow, the Wise One, the Lover — figures that appear across cultures and centuries because they live in all of us.",
      "A recurring dream, in Jung's view, is rarely a problem to solve. It's often the psyche returning to an image because something in us is still waiting to be welcomed, integrated, or simply witnessed.",
    ],
  },
  {
    title: "Modern science",
    emoji: "🧠",
    body: "Sleep researchers see dreaming as the brain weaving memory, emotion and meaning — helping us process the day and regulate our nervous system.",
    more: [
      "During REM sleep, the brain becomes remarkably active. Memory networks, emotional centres and meaning-making regions all light up together, while the parts that normally hold us to strict logic quiet down.",
      "Researchers think this is partly how the brain digests experience: sorting what to keep, softening emotional intensity, and stitching new events into the longer story of who we are.",
      "It's also one reason dreams can feel strange or symbolic. The mind is associating, not narrating — linking a feeling from today with an image from years ago because they share the same emotional texture.",
    ],
  },
  {
    title: "Trauma & dreams",
    emoji: "🤍",
    body: "After trauma, dreams may carry strong emotion, repetition or vivid imagery. This is the body's way of trying to integrate — not a sign something is wrong with you.",
    more: [
      "When something has overwhelmed the nervous system, the mind sometimes returns to it in sleep — through fragments, sensations, or dreams that echo the original feeling rather than the original event.",
      "This can look like nightmares, recurring scenes, sudden waking, or dreams that feel heavier than the day they followed. None of this means you are broken. It often means your system is still working on something that hasn't finished moving through.",
      "Gentle witnessing helps more than analysis here. Grounding before sleep, soft light on waking, a few slow breaths, and writing only as much as feels safe — these small acts can let the body know it is no longer alone with what it carries.",
      "If dreams feel destabilising or persistently distressing, working with a trauma-informed therapist can offer support that journaling alone is not meant to replace.",
    ],
  },
];


type DreamSymbol = {
  symbol: string;
  emoji: string;
  reflection: string;
  themes: string[];
  questions: string[];
  keywords?: string[];
};

const DREAM_SYMBOLS: DreamSymbol[] = [
  {
    symbol: "Water",
    emoji: "🌊",
    reflection:
      "Water in dreams may sometimes connect with emotions, change, overwhelm, cleansing, or the unknown. For some people, calm water may feel peaceful, while stormy water may feel intense or hard to manage.",
    themes: ["Emotion", "Change", "Overwhelm", "Cleansing", "The unknown"],
    questions: [
      "What did the water feel like in the dream?",
      "Were you safe, overwhelmed, floating, drowning, or watching from a distance?",
      "Does the water remind you of any emotion in your waking life?",
    ],
    keywords: ["ocean", "river", "sea", "rain", "flood"],
  },
  {
    symbol: "House",
    emoji: "🏠",
    reflection:
      "A house may sometimes reflect a sense of self, safety, or different parts of your inner world. For some people, familiar rooms feel comforting, while unknown rooms may feel curious or unsettling.",
    themes: ["Self", "Safety", "Inner world", "Belonging"],
    questions: [
      "How did the house feel — welcoming, unfamiliar, or in-between?",
      "Were there rooms you wanted to explore, or rooms you wanted to avoid?",
      "Does any part of the house echo something in your waking life?",
    ],
    keywords: ["home", "room", "hallway", "kitchen"],
  },
  {
    symbol: "Door",
    emoji: "🚪",
    reflection:
      "Doors may sometimes invite reflection on transitions, choices, or what feels open or closed right now. For some people, an open door feels inviting, while a locked door may feel protective or limiting.",
    themes: ["Transition", "Choice", "Boundaries", "Possibility"],
    questions: [
      "Was the door open, closed, or somewhere in between?",
      "Did you want to go through it, or stay where you were?",
      "Is there a threshold in your waking life you are gently considering?",
    ],
    keywords: ["gate", "entrance", "threshold"],
  },
  {
    symbol: "Child",
    emoji: "🧒",
    reflection:
      "A child in a dream may sometimes connect with tenderness, younger parts of yourself, or feelings that long to be held with care. Only take what feels right — your own meaning matters most.",
    themes: ["Tenderness", "Inner child", "Vulnerability", "Care"],
    questions: [
      "What did the child seem to need or feel?",
      "How did you respond to them in the dream?",
      "Is there a softer part of you asking for gentleness right now?",
    ],
    keywords: ["kid", "baby", "infant", "young"],
  },
  {
    symbol: "Animal",
    emoji: "🦌",
    reflection:
      "Animals in dreams may sometimes carry instinct, protection, or qualities you recognise in yourself. For some people, a calm animal feels reassuring, while a frightened or fierce animal may feel activating.",
    themes: ["Instinct", "Protection", "Wildness", "Companionship"],
    questions: [
      "How did the animal feel toward you — safe, wary, distant, close?",
      "What quality of this animal stays with you most?",
      "Is there a part of you that moves a little like this animal?",
    ],
    keywords: ["dog", "cat", "wolf", "bird", "snake", "horse"],
  },
  {
    symbol: "Falling",
    emoji: "🪂",
    reflection:
      "Falling may sometimes reflect feeling unsupported, a loss of control, or a moment of release. For some people, falling feels frightening, while for others it can feel surprisingly like letting go.",
    themes: ["Loss of control", "Letting go", "Support", "Surrender"],
    questions: [
      "What was the falling like — sudden, slow, endless, gentle?",
      "Was anything holding you, or were you alone?",
      "Where in your waking life might you be holding on very tightly?",
    ],
  },
  {
    symbol: "Being chased",
    emoji: "👣",
    reflection:
      "Being chased may sometimes point to a feeling, memory, or part of yourself that wants attention rather than escape. Only take what feels right, and move at your own pace.",
    themes: ["Avoidance", "Fear", "Attention", "Self-protection"],
    questions: [
      "What was chasing you, and how close did it feel?",
      "Was there anywhere in the dream that felt safer?",
      "Is there something in your waking life that feels hard to turn toward?",
    ],
    keywords: ["chase", "running", "pursued"],
  },
  {
    symbol: "Teeth",
    emoji: "🦷",
    reflection:
      "Teeth dreams are very common and may sometimes connect with feelings about voice, change, vulnerability, or how you appear to others. For some people, they reflect a season of transition.",
    themes: ["Voice", "Change", "Vulnerability", "Self-image"],
    questions: [
      "What was happening with the teeth — falling, breaking, growing?",
      "How did you feel in the dream — embarrassed, calm, afraid, curious?",
      "Is there something you have been wanting to express or release?",
    ],
    keywords: ["tooth", "mouth"],
  },
  {
    symbol: "Death",
    emoji: "🕊️",
    reflection:
      "Death in dreams may sometimes symbolise endings, transitions, or a part of life that is changing shape. It does not usually predict literal events. Only take what feels right, and go gently.",
    themes: ["Endings", "Transformation", "Grief", "Letting go"],
    questions: [
      "Whose ending or change appeared in the dream?",
      "What feelings stayed with you when you woke?",
      "Is something in your life quietly shifting or coming to a close?",
    ],
    keywords: ["dying", "funeral", "loss"],
  },
  {
    symbol: "Mother",
    emoji: "🌷",
    reflection:
      "A mother figure in a dream may sometimes connect with feelings about care, safety, comfort, or longing. Mother symbols can feel tender for many people, especially around grief or complicated relationships. Move at your own pace.",
    themes: ["Care", "Safety", "Longing", "Comfort"],
    questions: [
      "How did the mother figure feel — present, distant, warm, absent?",
      "What feeling stayed strongest after the dream?",
      "What kind of care might you be needing right now?",
    ],
    keywords: ["mum", "mom", "mama"],
  },
  {
    symbol: "Father",
    emoji: "🌳",
    reflection:
      "A father figure may sometimes connect with themes of protection, authority, support, or absence. These dreams can feel layered, especially around complicated relationships. Be gentle with yourself.",
    themes: ["Protection", "Authority", "Support", "Absence"],
    questions: [
      "How did the father figure feel toward you in the dream?",
      "What did you wish you could say, hear, or feel?",
      "Where in your life might you be longing for steadiness?",
    ],
    keywords: ["dad", "papa"],
  },
  {
    symbol: "Light",
    emoji: "🕯️",
    reflection:
      "Light may sometimes appear as a quiet sense of hope, clarity, or an inner knowing returning. For some people, even a small light in a dream can feel meaningful.",
    themes: ["Hope", "Clarity", "Guidance", "Inner knowing"],
    questions: [
      "Where was the light coming from, and how did it feel?",
      "Did it change anything in the dream when it appeared?",
      "Is there a small light in your waking life asking to be trusted?",
    ],
    keywords: ["candle", "sun", "lamp"],
  },
];

function DreamsPage() {
  const [view, setView] = useState<View>({ kind: "home" });
  // Every navigation inside the Dream Journal lands at the top of the new step.
  useScrollTopOnChange(
    view.kind === "home"
      ? "home"
      : view.kind === "compose"
        ? `compose-${view.mode}-${view.category ?? ""}`
        : `${view.kind}-${view.dream.id}`,
  );

  const [showAllRecent, setShowAllRecent] = useState(false);
  const [symbolExplorerOpen, setSymbolExplorerOpen] = useState(false);

  const [dreams, setDreams] = useState<DreamRow[] | null>(null);
  const [dreamsLoadFailed, setDreamsLoadFailed] = useState(false);
  const [patterns, setPatterns] = useState<DreamPatterns | null>(null);

  const listFn = useServerFn(listMyDreams);
  const patternsFn = useServerFn(getDreamPatterns);
  const deleteFn = useServerFn(deleteDream);

  const refreshDreams = async () => {
    try {
      const res = await listFn();
      setDreams(res.dreams);
      setDreamsLoadFailed(false);
    } catch {
      // Surface the failure instead of rendering the empty state, which would
      // read to a member as "your dreams are gone".
      setDreams([]);
      setDreamsLoadFailed(true);
    }
  };

  const refreshPatterns = async () => {
    try {
      const res = await patternsFn();
      setPatterns(res.patterns);
    } catch {
      setPatterns(EMPTY_DREAM_PATTERNS);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const session = getStoredSession();
      if (cancelled) return;
      if (!session) {
        setDreams([]);
        setDreamsLoadFailed(false);
        setPatterns(EMPTY_DREAM_PATTERNS);
        return;
      }
      void refreshDreams();
      void refreshPatterns();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div className="relative min-h-dvh overflow-hidden bg-background">
      {/* Atmospheric layer */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -start-20 h-72 w-72 rounded-full bg-[#cfe0f3] opacity-50 blur-3xl" />
        <div className="absolute top-40 -end-16 h-80 w-80 rounded-full bg-[#e3d5f5] opacity-50 blur-3xl" />
        <div className="absolute bottom-0 start-1/3 h-64 w-64 rounded-full bg-white opacity-60 blur-3xl dark:bg-white/10 dark:opacity-30" />
        <Moon
          className="absolute top-10 end-10 h-10 w-10 text-[#b9c8df] opacity-70 animate-pulse"
          style={{ animationDuration: "5s" }}
        />
        <Cloud className="absolute top-28 start-12 h-12 w-12 text-white opacity-70" />
        <Cloud className="absolute top-[55%] end-20 h-10 w-10 text-white opacity-60" />
        {[...Array(14)].map((_, i) => (
          <Star
            key={i}
            className="absolute text-[#b9a8d9]"
            style={{
              top: `${(i * 53) % 95}%`,
              left: `${(i * 71) % 95}%`,
              width: `${6 + (i % 4) * 3}px`,
              height: `${6 + (i % 4) * 3}px`,
              opacity: 0.35 + ((i * 13) % 40) / 100,
              animation: `pulse ${3 + (i % 4)}s ease-in-out ${i * 0.3}s infinite`,
            }}
            fill="currentColor"
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-3xl px-4 pt-6 pb-12 md:pt-12">
        {view.kind === "home" && (
          <>
            {/* ───── Hero ───── */}
            <header className="mb-6 text-center">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-card/80 px-3 py-1 text-xs text-foreground/80 backdrop-blur">
                <Moon className="h-3.5 w-3.5 text-[var(--gold-deep)]" /> Dream Journal
              </div>
              <h1 className="font-display text-[1.7rem] leading-tight tracking-tight text-foreground md:text-5xl">
                A quiet space for your dreams
              </h1>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-foreground/70">
                Record what you remember. Explore emotions, symbols and recurring themes — at your own pace.
              </p>

              <div className="mt-5 flex flex-col items-stretch justify-center gap-2.5 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setView({ kind: "compose", mode: "write" })}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#7a6ca8] to-[#a88dc4] px-6 text-sm font-medium text-white shadow-md transition hover:opacity-90"
                >
                  ＋ Record a Dream
                </button>
                <button
                  type="button"
                  onClick={() => setView({ kind: "compose", mode: "voice" })}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-gold/40 bg-card/80 px-5 text-sm font-medium text-foreground backdrop-blur transition hover:bg-gold/10"
                >
                  <Mic className="h-4 w-4 text-[var(--gold-deep)]" /> Record by Voice
                </button>
              </div>

              <p className="mt-3 text-xs leading-relaxed text-foreground/60">
                🔒 Private to you
                <br />
                Your dream journal entries are visible only to you.
              </p>
            </header>

            {/* ───── Jana's note ───── */}
            <JanaNote />

            {/* ───── What would you like to record? ───── */}
            <section className="mb-6">
              <h2 className="mb-3 px-1 font-display text-lg text-foreground md:text-xl">
                🌙 What would you like to record?
              </h2>
              <div className="grid grid-cols-2 gap-2.5 md:gap-3">
                {DREAM_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() =>
                      setView({ kind: "compose", mode: "write", category: c.id })
                    }
                    className="group flex min-h-[118px] flex-col items-start gap-1.5 rounded-2xl border border-gold/25 bg-card/80 p-3.5 text-start shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-[var(--gold-deep)]/60 hover:bg-card md:p-4"
                  >
                    <span className="text-2xl md:text-3xl" aria-hidden>{c.emoji}</span>
                    <span className="font-display text-sm font-medium text-foreground md:text-base">
                      {c.label}
                    </span>
                    <span className="text-[11px] leading-snug text-foreground/65 md:text-xs">
                      {c.hint}
                    </span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setView({ kind: "compose", mode: "voice" })}
                className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-gold/40 bg-card px-4 text-sm font-medium text-foreground transition hover:bg-gold/10"
              >
                <Mic className="h-4 w-4 text-[var(--gold-deep)]" />
                Rather speak than type? — Record by Voice
              </button>
            </section>

            {/* ───── Before you begin (expandable) ───── */}
            <ExpandableCard
              className="mb-6"
              title="💛 A gentle note before you begin"
            >
              <p className="text-sm leading-relaxed text-foreground/80">
                Dreams can stir emotions, memories or sensations. Move at your own pace, pause
                whenever you need, and take what helps and leave what doesn't.
              </p>
              <div className="mt-3">
                <JournalGuide journal="dreams" />
              </div>
              <div className="mt-3">
                <PrivacyNotice>
                  Your dream journal entries are private and visible only to you.
                </PrivacyNotice>
              </div>
            </ExpandableCard>

            {/* ───── Recent dreams ───── */}
            <section className="mb-7">
              <div className="mb-2 flex items-center justify-between px-1">
                <h2 className="font-display text-lg text-foreground md:text-xl">
                  🌙 Recent dreams
                </h2>
                {dreams && dreams.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllRecent((v) => !v)}
                    className="text-xs font-medium text-foreground/70 hover:text-foreground hover:underline"
                  >
                    {showAllRecent ? "Show less" : "View all dreams →"}
                  </button>
                )}
              </div>
              <RecentList
                dreams={dreams}
                loadFailed={dreamsLoadFailed}
                onRetry={() => {
                  setDreams(null);
                  void refreshDreams();
                }}
                limit={showAllRecent ? undefined : 3}
                carousel={!showAllRecent}
                onOpen={(d) => setView({ kind: "dream", dream: d })}
                onDelete={async (id) => {
                  try {
                    await ensureFreshSession();
                    await deleteFn({ data: { id } });
                    await refreshDreams();
                    await refreshPatterns();
                    toast.success("Dream deleted");
                  } catch (err: unknown) {
                    const raw = err instanceof Error ? err.message : "";
                    toast.error(raw || "Couldn't delete dream — please try again.");
                  }
                }}
              />
            </section>

            {/* ───── Your Dream Patterns (only when there's enough to notice) ───── */}
            {patterns && patterns.totals.dreams >= 3 && (
              <section id="dream-patterns" className="mb-7 scroll-mt-4">
                <div className="mb-2 px-1">
                  <h2 className="font-display text-lg text-foreground md:text-xl">
                    ✨ Your Dream Patterns
                  </h2>
                  <p className="text-xs text-foreground/60 md:text-sm">
                    Gentle observations from the dreams you've recorded.
                  </p>
                </div>
                <PatternsInsights patterns={patterns} />
              </section>
            )}

            {/* ───── Symbol Explorer ───── */}
            <section className="mb-7">
              <div className="rounded-2xl border border-gold/25 bg-card/80 shadow-sm backdrop-blur">
                {!symbolExplorerOpen ? (
                  <button
                    type="button"
                    onClick={() => setSymbolExplorerOpen(true)}
                    className="w-full cursor-pointer px-4 py-4 text-start md:px-5"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-lg" aria-hidden>🔮</span>
                      <span className="font-display text-base text-foreground md:text-lg">
                        Curious about a symbol?
                      </span>
                    </span>
                    <span className="mt-1.5 block text-xs leading-relaxed text-foreground/70 md:text-sm">
                      Explore possible psychological, cultural and personal associations — without
                      treating any interpretation as the answer. You decide what a symbol means to you.
                    </span>
                    <span className="mt-2.5 inline-flex items-center gap-1 text-xs font-medium text-[var(--gold-deep)]">
                      Explore a symbol <ChevronRight className="h-3.5 w-3.5" />
                    </span>
                  </button>
                ) : (
                  <div>
                    <div className="flex items-center justify-between gap-3 border-b border-gold/20 px-4 py-3 md:px-5 md:py-3.5">
                      <span className="flex items-center gap-2">
                        <span className="text-lg" aria-hidden>🔮</span>
                        <span className="font-display text-base text-foreground md:text-lg">
                          Symbol Explorer
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setSymbolExplorerOpen(false)}
                        className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-gold/30 bg-card/80 px-3 py-1.5 text-xs text-foreground/80 transition hover:bg-card hover:text-foreground"
                        aria-label="Close Symbol Explorer"
                      >
                        <X className="h-4 w-4" />
                        Close
                      </button>
                    </div>
                    <div className="px-4 py-4 md:px-5 md:py-5">
                      <p className="mb-4 text-xs leading-relaxed text-foreground/65 md:text-sm">
                        A quiet companion for when you'd like to sit with a recurring image or symbol from your dreams. You remain the one who decides what it means to you.
                      </p>
                      <SymbolExplorer
                        autoFocusSearch
                        onSit={(symbol) =>
                          setView({ kind: "compose", mode: "write", category: `Symbol Exploration — ${symbol}` })
                        }
                      />
                      <div className="mt-6 flex justify-center">
                        <button
                          type="button"
                          onClick={() => setSymbolExplorerOpen(false)}
                          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-gold/30 bg-card/80 px-4 py-2 text-sm text-foreground/80 transition hover:bg-card hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                          Close Symbol Explorer
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* ───── Four ways of looking at dreams ───── */}
            <section className="mb-7">
              <div className="mb-3 px-1">
                <h2 className="font-display text-lg text-foreground md:text-xl">
                  📚 Four ways of looking at dreams
                </h2>
                <p className="text-xs text-foreground/60 md:text-sm">
                  Historical and scientific perspectives — none of them a settled answer. Swipe to explore →
                </p>
              </div>
              <SwipeRail label="Dream perspectives">
                {DREAM_LENSES.map((l) => (
                  <LensCard key={l.title} lens={l} />
                ))}
              </SwipeRail>
            </section>

            {/* ───── Support after a dream ───── */}
            <section className="mb-7">
              <div className="mb-3 px-1">
                <h2 className="font-display text-lg text-foreground md:text-xl">
                  🤍 What would help right now?
                </h2>
                <p className="text-xs text-foreground/60 md:text-sm">
                  If a dream has left you unsettled, everything is one tap away.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {DREAM_SUPPORT_LINKS.map((s) => (
                  <Link
                    key={s.label}
                    to={s.to}
                    className="flex items-center gap-3 rounded-2xl border border-gold/25 bg-card/80 px-4 py-3 text-start shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:bg-card"
                  >
                    <span className="text-xl" aria-hidden>{s.emoji}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">{s.label}</span>
                      <span className="block text-[11px] text-foreground/60">{s.dest}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-foreground/40" />
                  </Link>
                ))}
              </div>
            </section>
          </>
        )}


        {view.kind === "compose" && (
          <ComposeView
            mode={view.mode}
            category={view.category}
            onCancel={() => setView({ kind: "home" })}
            onSaved={(dream, analysis) => {
              // Switch to the reflection view immediately so a slow background
              // refresh (or an unresolved auth state in the preview iframe)
              // can never leave the user stuck on the save form.
              setView({ kind: "reflect", dream, analysis });
              void refreshDreams();
              void refreshPatterns();
            }}
          />
        )}

        {view.kind === "reflect" && (
          <ReflectView
            dream={view.dream}
            analysis={view.analysis}
            onDone={() => {
              setView({ kind: "home" });
              void refreshDreams();
            }}
          />
        )}

        {view.kind === "dream" && (
          <DreamDetailView
            dream={view.dream}
            onBack={() => setView({ kind: "home" })}
            onReflectionSaved={(updated) => {
              setView({ kind: "dream", dream: updated });
              void refreshDreams();
            }}
          />
        )}

        <div className="mt-8 rounded-2xl border border-gold/20 bg-card/60 p-3.5 text-xs leading-relaxed text-foreground/65 backdrop-blur">
          <p className="font-medium text-foreground/80">About dream reflections</p>
          <p className="mt-1">
            Dream reflections are exploratory prompts, not medical advice, diagnosis or therapy.
            There is no single correct interpretation of a dream, and you remain the expert on
            your own experience.
          </p>
          <p className="mt-2">
            <Link to="/crisis" className="font-medium text-[var(--gold-deep)] underline">
              Crisis resources
            </Link>
            {" · "}
            <Link to="/library" className="font-medium text-[var(--gold-deep)] underline">
              Library → Dreams &amp; Sleep
            </Link>
            {" · "}
            <Link to="/sleep-dreams" className="font-medium text-[var(--gold-deep)] underline">
              Sleep &amp; Rest
            </Link>
          </p>
        </div>

        <div className="mt-4">
          <ComplianceNotice variant="journal" />
        </div>

      </div>
    </div>
  );
}

/* ───────────────── Small shared cards ───────────────── */
function JanaNote() {
  const [open, setOpen] = useState(false);
  return (
    <aside className="mb-6 overflow-hidden rounded-2xl border border-[var(--gold-deep)]/35 bg-gradient-to-br from-gold/12 to-cream/40 shadow-sm backdrop-blur">
      <div className="px-4 py-4 md:px-5">
        <div className="flex items-center gap-2">
          <span className="text-lg" aria-hidden>💛</span>
          <h2 className="font-display text-base text-foreground md:text-lg">A note from Jana</h2>
        </div>
        <p className="mt-1 text-[11px] uppercase tracking-wide text-foreground/55">
          A personal reflection — not clinical or therapeutic guidance
        </p>

        <blockquote className="mt-2.5 space-y-2 text-sm italic leading-relaxed text-foreground/85">
          <p>
            “Dreams helped me a lot in my own trauma healing. Over time, paying attention to them
            helped me understand parts of my unconscious mind — emotions, fears and patterns that I
            wasn't always able to recognise during the day.
            {!open && "”"}
          </p>
          {open && (
            <>
              <p>
                I don't believe every dream needs to be analysed or given a fixed meaning. For me,
                the value was learning to become curious about what my inner world might be
                communicating.
              </p>
              <p>
                That's why I wanted the Dream Journal to be part of Recovery Trauma™ — as a private
                space to notice, reflect and discover what your dreams may mean to you.”
              </p>
            </>
          )}
        </blockquote>

        <div className="mt-2.5 flex items-center justify-between gap-3">
          <p className="text-xs font-medium text-foreground/70">
            — Jana, Founder of Recovery Trauma™
          </p>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="shrink-0 text-xs font-medium text-[var(--gold-deep)] hover:underline"
          >
            {open ? "Show less" : "Read more"}
          </button>
        </div>
      </div>
    </aside>
  );
}

function ExpandableCard({
  title,
  children,
  className = "",
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-2xl border border-gold/25 bg-card/80 shadow-sm backdrop-blur ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-start md:px-5"
      >
        <span className="font-display text-sm text-foreground md:text-base">{title}</span>
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-foreground/50 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && <div className="border-t border-gold/20 px-4 py-4 md:px-5">{children}</div>}
    </div>
  );
}


/* ───────────────── Compose view ───────────────── */
function ComposeView({
  mode,
  category,
  onCancel,
  onSaved,
}: {
  mode: "write" | "voice";
  category?: string;
  onCancel: () => void;
  onSaved: (dream: DreamRow, analysis: DreamAnalysis) => void;
}) {
  const analyze = useServerFn(analyzeDream);
  const save = useServerFn(saveDream);

  const [dream, setDream] = useState("");
  const [mood, setMood] = useState<string>("");
  const [title, setTitle] = useState(category ? `${category} — ` : "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSafety, setShowSafety] = useState(false);
  const [showVoice, setShowVoice] = useState(mode === "voice");
  const [isAuthed, setIsAuthed] = useState<boolean | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);
  // AI disclosure: dream text is only sent to the AI service once the member
  // has agreed to the inline consent (App Review Guideline 5.1.2). Declining
  // still saves the dream — it simply uses the local, offline reflection.
  const [aiConsentOpen, setAiConsentOpen] = useState(false);
  const aiConsentResolve = useRef<((granted: boolean) => void) | null>(null);
  const askAiConsent = () =>
    hasAiConsent()
      ? Promise.resolve(true)
      : new Promise<boolean>((resolve) => {
          aiConsentResolve.current = resolve;
          setAiConsentOpen(true);
        });

  // ── Draft safety net ───────────────────────────────────────────────
  // Nothing typed here may ever be lost. iOS Safari discards backgrounded
  // tabs, saves can time out, and people navigate away mid-sentence — so the
  // composer keeps a local draft and only clears it after a confirmed save.
  useEffect(() => {
    const saved = readDreamDraft();
    if (!saved) return;
    if (saved.dream) {
      setDream(saved.dream);
      setDraftRestored(true);
    }
    if (saved.mood) setMood(saved.mood);
    if (saved.title) setTitle(saved.title);
  }, []);

  useEffect(() => {
    if (!dream.trim() && !title.trim() && !mood) return;
    const id = window.setTimeout(
      () => writeDreamDraft({ dream, mood, title }),
      400,
    );
    return () => window.clearTimeout(id);
  }, [dream, mood, title]);

  useEffect(() => {
    let active = true;
    // Read from localStorage synchronously to avoid supabase.auth.getSession()
    // hangs on mobile Safari/Chrome. Keep an onAuthStateChange listener so
    // sign-in/out inside the tab updates the composer.
    setIsAuthed(!!readStoredAuthSession());
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (active) setIsAuthed(!!session);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);


  async function onSubmit(e?: React.FormEvent | React.MouseEvent) {
    e?.preventDefault?.();
    if (loading) return;
    if (dream.trim().length < 10) {
      setError("Add a few more words about your dream so we can save it.");
      return;
    }
    void reportCrisisIfDetected({ text: dream, source: "dream" }).then((a) => {
      if (a.detected) setShowSafety(true);
    });
    if (detectCrisis(dream)) setShowSafety(true);

    setError(null);
    setLoading(true);

    // Watchdog — unconditionally unblock the UI after 30s. Late resolutions
    // are no-ops because `loading` will already be false.
    const watchdog = window.setTimeout(() => {
      setLoading(false);
      setError(
        "Saving is taking longer than expected. Your words are kept safely on this device, so nothing is lost — please try again in a moment.",
      );
    }, 30_000);

    try {
      // Auth state is tracked via onAuthStateChange, but that flag can still
      // be stale (it is seeded once on mount). Re-read the stored session at
      // submit time so a signed-in member is never silently downgraded to a
      // "guest" dream that is shown as saved but never written to the cloud.
      const authedNow = isAuthed === true || !!readStoredAuthSession();

      if (!authedNow) {

        const analysis = createGuestDreamAnalysis(dream.trim(), mood || undefined);
        const finalTitle = title.trim() || analysis.suggestedTitle;
        onSaved(
          {
            id: `guest-${Date.now()}`,
            title: finalTitle,
            body: dream.trim(),
            mood: mood || null,
            dreamed_at: new Date().toISOString(),
            reflection: [],
            tags: analysis.tags,
            created_at: new Date().toISOString(),
          },
          analysis,
        );
        return;
      }

      const timeout = <T,>(p: Promise<T>, ms: number, label: string) =>
        Promise.race<T>([
          p,
          new Promise<T>((_, reject) =>
            window.setTimeout(
              () => reject(new Error(`${label} timeout`)),
              ms,
            ),
          ),
        ]);

      // Always run a fast local analysis so we have safe defaults even if
      // the AI gateway hangs or refuses.
      const localAnalysis = createGuestDreamAnalysis(dream.trim(), mood || undefined);

      // Try the AI reflection but never block the save on it.
      let analysis = localAnalysis;
      try {
        if (!(await askAiConsent())) throw new Error("ai-consent-declined");
        const aiRes = await timeout(
          analyze({ data: { dream: dream.trim(), mood: mood || undefined } }),
          15_000,
          "Analyze",
        );
        analysis = aiRes.analysis;
      } catch (aiErr) {
        console.warn("[dreams] AI reflection unavailable, using local fallback", aiErr);
      }

      const finalTitle = title.trim() || analysis.suggestedTitle;

      // Refresh the access token immediately before the protected save call.
      // Cached tokens often expire while mobile tabs are backgrounded;
      // getSession() auto-refreshes and surfaces a clear sign-in prompt
      // instead of a silent 401.
      await ensureFreshSession();

      const saved = await timeout(
        save({
          data: {
            title: finalTitle,
            body: dream.trim(),
            mood: mood || undefined,
            tags: analysis.tags,
          },
        }),
        15_000,
        "Save",
      );

      const dreamRow: DreamRow = {
        id: saved.id,
        title: finalTitle,
        body: dream.trim(),
        mood: mood || null,
        dreamed_at: new Date().toISOString(),
        reflection: [],
        tags: analysis.tags,
        created_at: new Date().toISOString(),
      };
      // Only now is the dream safely in the cloud — release the local draft.
      clearDreamDraft();
      toast.success(saved.duplicate ? "Dream already saved" : "Dream saved");

      notifyActivityChange("dream");
      onSaved(dreamRow, analysis);
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : "";
      if (/unauthor/i.test(raw) || /no authorization/i.test(raw)) {
        setError(
          "Sign in to save your dream and generate a reflection. Your words are kept safely on this device until you do — nothing is lost.",
        );
        toast.error("Sign in to save your dream", {
          description: raw || undefined,
        });
      } else if (/timeout/i.test(raw)) {
        setError(
          "Saving is taking longer than expected. Your words are kept safely on this device — please try again in a moment.",
        );
        toast.error("Save timed out — your dream is kept, please try again.", {
          description: raw || undefined,
        });
      } else {
        setError(
          `${raw || "Something went wrong."} Your words are kept safely on this device — please try again.`,
        );
        toast.error(raw || "Couldn't save dream — please try again.", {
          description: raw ? "Server response above. Try again in a moment." : undefined,
        });
      }

    } finally {
      window.clearTimeout(watchdog);
      setLoading(false);
    }
  }


  return (
    <div>
      <button
        onClick={onCancel}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#5a5775] hover:text-[#2c2a4a] dark:text-muted-foreground dark:hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <form
        onSubmit={onSubmit}
        className="rounded-2xl border border-white/70 bg-white/70 p-6 shadow-sm backdrop-blur-md dark:border-border dark:bg-card"
      >
        {isAuthed === false && (
          <div className="mb-4 rounded-xl border border-[#b9a8d9]/60 bg-[#f3edfb] px-4 py-3 text-sm text-[#2c2a4a] dark:border-border dark:bg-card dark:text-foreground">
            <span>
              Guest testing is on. You can try the reflection flow now; sign in only when you want
              to save dreams privately.
            </span>
          </div>
        )}
        {draftRestored && (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-[#b9a8d9]/60 bg-white/80 px-4 py-3 text-sm text-[#2c2a4a] dark:border-border dark:bg-background dark:text-foreground">
            <span>
              We kept what you were writing last time, so nothing was lost. Carry on whenever
              you're ready.
            </span>
            <button
              type="button"
              onClick={() => {
                setDream("");
                setMood("");
                setTitle(category ? `${category} — ` : "");
                clearDreamDraft();
                setDraftRestored(false);
              }}
              className="shrink-0 rounded-full border border-[#b9a8d9]/60 px-3 py-1 text-xs font-medium text-[#5a4b7a] transition hover:bg-[#f3edfb] dark:border-border dark:text-muted-foreground dark:hover:bg-accent dark:hover:text-accent-foreground"
            >
              Start fresh
            </button>
          </div>
        )}

        <div className="mb-2 flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-[#2c2a4a] dark:text-foreground">
            <Moon className="h-4 w-4 text-[#7a6ca8]" /> Your dream
          </label>
          <button
            type="button"
            onClick={() => setShowVoice(true)}
            className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-[#b9a8d9]/60 bg-white/80 px-3 py-1 text-xs font-medium text-[#5a4b7a] transition hover:bg-[#f3edfb] dark:border-border dark:bg-background dark:text-muted-foreground dark:hover:bg-accent dark:hover:text-accent-foreground"
          >
            <Mic className="h-3.5 w-3.5" /> Record
          </button>
        </div>
        <textarea
          value={dream}
          onChange={(e) => setDream(e.target.value)}
          rows={7}
          placeholder="I was walking through an old house I'd never seen before…"
          className="w-full resize-y rounded-xl border border-[#dcd6ea] bg-white/80 p-3 text-sm text-[#2c2a4a] placeholder:text-[#777189] focus:outline-none focus:ring-2 focus:ring-[#b9a8d9] dark:border-border dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground dark:focus:ring-gold"
          maxLength={8000}
        />

        <div className="mt-4">
          <label className="mb-1 block text-xs font-medium text-[#5a4b7a] dark:text-muted-foreground">Title (optional)</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="We'll suggest one if you leave it empty"
            className="w-full rounded-xl border border-[#dcd6ea] bg-white/80 p-2.5 text-sm text-[#2c2a4a] placeholder:text-[#777189] focus:outline-none focus:ring-2 focus:ring-[#b9a8d9] dark:border-border dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground dark:focus:ring-gold"
            maxLength={80}
          />
        </div>

        <div className="mt-5">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#2c2a4a] dark:text-foreground">
            <Heart className="h-4 w-4 text-[#a88dc4]" /> Mood on waking
          </div>
          <div className="flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <button
                type="button"
                key={m}
                onClick={() => setMood(mood === m ? "" : m)}
                className={[
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition-all",
                  mood === m
                    ? "border-transparent bg-[#7a6ca8] text-white shadow"
                    : "border-[#dcd6ea] bg-white/70 text-[#5a5775] hover:text-[#2c2a4a] dark:border-border dark:bg-background dark:text-muted-foreground dark:hover:text-foreground",
                ].join(" ")}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <span>{error}</span>
            {/sign in/i.test(error) && (
              <Link
                to="/login"
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#7a6ca8] px-3 py-1 text-xs font-semibold text-white hover:opacity-90"
              >
                Sign in
              </Link>
            )}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end">
          <button
            type="button"
            disabled={loading}
            onClick={(ev) => {
              // Always handle the save in onClick so iOS Safari's first-tap
              // form-submit quirk can't swallow the action. Blur any active
              // input first so the keyboard releases cleanly.
              if (loading) return;
              ev.preventDefault();
              if (
                typeof document !== "undefined" &&
                document.activeElement instanceof HTMLElement
              ) {
                document.activeElement.blur();
              }
              void onSubmit(ev);
            }}
            className="relative z-20 inline-flex min-h-[48px] touch-manipulation items-center gap-2 rounded-full bg-gradient-to-r from-[#7a6ca8] to-[#a88dc4] px-6 py-3 text-sm font-medium text-white shadow-md transition hover:opacity-90 disabled:opacity-60 sm:min-h-0 sm:py-2.5"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" /> Save dream & reflect
              </>
            )}
          </button>
        </div>

      </form>

      {showSafety && (
        <CrisisDialog
          footerNote="Your dream is being saved."
          onClose={() => setShowSafety(false)}
        />
      )}

      <AiConsentDialog
        open={aiConsentOpen}
        onAgree={() => {
          setAiConsentOpen(false);
          aiConsentResolve.current?.(true);
          aiConsentResolve.current = null;
        }}
        onDecline={() => {
          setAiConsentOpen(false);
          aiConsentResolve.current?.(false);
          aiConsentResolve.current = null;
        }}
      />

      <VoiceRecorder
        open={showVoice}
        onClose={() => setShowVoice(false)}
        onTranscript={(text) => {
          setDream((prev) => (prev.trim() ? prev.trim() + "\n\n" + text : text));
        }}
        title="Record your dream"
        description="Speak what you remember — even fragments. You can edit before saving."
        accent="lavender"
      />
    </div>
  );
}

/* ───────────────── Reflect view (3-4 prompts) ───────────────── */
function ReflectView({
  dream,
  analysis,
  onDone,
}: {
  dream: DreamRow;
  analysis: DreamAnalysis;
  onDone: () => void;
}) {
  const update = useServerFn(updateDreamReflection);
  const questions = useMemo(
    () =>
      analysis.reflectionQuestions.length > 0
        ? analysis.reflectionQuestions
        : GENTLE_DREAM_QUESTIONS,
    [analysis.reflectionQuestions],
  );
  const [answers, setAnswers] = useState<string[]>(() => questions.map(() => ""));
  const [step, setStep] = useState(0);
  const [showAi, setShowAi] = useState(false);
  const [saving, setSaving] = useState(false);
  const atEnd = step >= questions.length;

  async function onSave() {
    if (saving) return;
    setSaving(true);
    try {
      const reflection = questions
        .map((q, i) => ({ question: q, answer: answers[i]?.trim() ?? "" }))
        .filter((r) => r.answer.length > 0);
      if (reflection.length > 0 && !dream.id.startsWith("guest-")) {
        toast.loading("Saving reflection…", { id: "dream-reflect-save" });
        await ensureFreshSession();
        await update({ data: { id: dream.id, reflection } });
        toast.success("Reflection saved", { id: "dream-reflect-save" });
      } else if (reflection.length === 0) {
        toast("Skipped — no reflection added.");
      }
      onDone();
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : "";
      toast.error(raw || "Couldn't save reflection — please try again.", {
        id: "dream-reflect-save",
        description: raw ? "Exact server response shown above." : undefined,
        duration: 8000,
      });
    } finally {
      setSaving(false);
    }
  }

  const q = questions[step];

  return (
    <div>
      <button
        onClick={onDone}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Skip reflection
      </button>

      <div className="mb-4">
        <h2 className="font-display text-xl text-foreground">🤍 Your reflection</h2>
        <p className="mt-1 text-sm text-foreground/70">
          Gentle, optional questions. Answer what you'd like and skip the rest.
        </p>
      </div>

      {!atEnd && q ? (
        <div className="rounded-2xl border border-gold/25 bg-card/80 p-5 shadow-sm backdrop-blur">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-[11px] uppercase tracking-wide text-foreground/50">
              Question {step + 1} of {questions.length}
            </span>
            <FavoriteButton
              storageKey="rt:fav-dream-prompts"
              id={q}
              label={q}
              sub="Dream prompt"
              to="/dreams"
              size="sm"
            />
          </div>
          <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-gold/15">
            <div
              className="h-full rounded-full bg-[var(--gold-deep)] transition-all"
              style={{ width: `${((step + 1) / questions.length) * 100}%` }}
            />
          </div>

          <label className="block font-display text-base text-foreground">{q}</label>
          <textarea
            rows={4}
            value={answers[step] ?? ""}
            onChange={(e) => {
              const next = [...answers];
              next[step] = e.target.value;
              setAnswers(next);
            }}
            className="mt-3 w-full resize-y rounded-xl border border-gold/25 bg-background/70 p-3 text-sm text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-gold/40"
            maxLength={2000}
            placeholder="A few words is enough…"
          />

          <div className="mt-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="text-sm text-foreground/60 disabled:opacity-40"
            >
              Back
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className="text-sm text-foreground/60 hover:text-foreground"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className="min-h-[44px] rounded-full bg-[var(--gold-deep)] px-5 text-sm font-medium text-background"
              >
                {step === questions.length - 1 ? "Finish" : "Next"}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setStep(questions.length)}
            className="mt-3 text-xs text-foreground/50 hover:underline"
          >
            Finish without answering the rest
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-2xl border border-gold/25 bg-card/80 p-5 shadow-sm backdrop-blur">
            <h3 className="font-display text-base text-foreground">Thank you for reflecting</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/75">
              Your words are private to you. Save them to keep them with this dream.
            </p>
            <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setStep(0)}
                className="rounded-full border border-gold/30 px-5 py-2.5 text-sm text-foreground/70"
              >
                Review answers
              </button>
              <button
                type="button"
                onClick={(ev) => {
                  if (saving) return;
                  ev.preventDefault();
                  if (
                    typeof document !== "undefined" &&
                    document.activeElement instanceof HTMLElement
                  ) {
                    document.activeElement.blur();
                  }
                  onSave();
                }}
                disabled={saving}
                className="inline-flex min-h-[48px] touch-manipulation items-center justify-center gap-2 rounded-full bg-[var(--gold-deep)] px-6 text-sm font-medium text-background disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                  </>
                ) : (
                  "Save reflection"
                )}
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-gold/25 bg-gold/5 p-5 backdrop-blur">
            <h3 className="font-display text-base text-foreground">✨ Reflect on this dream</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/75">
              Optional. A few exploratory prompts — never a meaning or an answer.
            </p>
            {!showAi ? (
              <button
                type="button"
                onClick={() => setShowAi(true)}
                className="mt-3 rounded-full border border-gold/35 px-5 py-2.5 text-sm font-medium text-[var(--gold-deep)]"
              >
                Show gentle reflections
              </button>
            ) : (
              <div className="mt-3 space-y-2.5">
                <p className="text-sm leading-relaxed text-foreground/80">{analysis.summary}</p>
                <p className="text-sm leading-relaxed text-foreground/70">
                  {analysis.emotionAcknowledgement}
                </p>
                {analysis.gentlePossibilities.length > 0 && (
                  <ul className="space-y-1.5 text-sm leading-relaxed text-foreground/80">
                    {analysis.gentlePossibilities.map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-2 inline-block h-1 w-1 rounded-full bg-[var(--gold-deep)]" />
                        <span>One possibility you might explore: {p}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs italic text-foreground/55">
                  Take what resonates. Skip what doesn't. There's no right answer.
                </p>
              </div>
            )}
          </div>

          <p className="rounded-2xl border border-gold/20 bg-card/70 p-4 text-center text-xs leading-relaxed text-foreground/65">
            {analysis.closingReminder}
          </p>
        </div>
      )}
    </div>
  );
}



/* ───────────────── Recent tab ───────────────── */
function dreamKind(d: DreamRow): { emoji: string; label: string } {
  const hay = `${d.title ?? ""}`.toLowerCase();
  if (hay.includes("nightmare")) return { emoji: "🌫️", label: "Nightmare" };
  if (hay.includes("recurring")) return { emoji: "🔄", label: "Recurring Dream" };
  if (hay.includes("lucid")) return { emoji: "✨", label: "Lucid Dream" };
  return { emoji: "🌙", label: "Dream" };
}

function relativeDate(iso: string): string {

  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return "Today";
  if (diff < 2 * day) return "Yesterday";
  if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))} weeks ago`;
  return new Date(iso).toLocaleDateString();
}

function RecentList({
  dreams,
  loadFailed,
  onRetry,
  limit,
  carousel = false,
  onOpen,
  onDelete,
}: {
  dreams: DreamRow[] | null;
  loadFailed?: boolean;
  onRetry?: () => void;
  limit?: number;
  carousel?: boolean;
  onOpen: (d: DreamRow) => void;
  onDelete: (id: string) => void;
}) {

  if (dreams === null) {
    return (
      <div className="rounded-2xl border border-white/70 bg-white/60 p-5 text-center text-sm text-[#5a5775] backdrop-blur-md dark:border-border dark:bg-card dark:text-muted-foreground">
        <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-[#7a6ca8]" />
        Loading your dreams…
      </div>
    );
  }
  // A failed load must never be dressed up as an empty journal — a member
  // seeing "record your first one" over dreams they know they wrote would
  // reasonably believe their private entries had been lost.
  if (loadFailed) {
    return (
      <div className="rounded-2xl border border-white/70 bg-white/60 p-5 text-center text-sm text-[#5a5775] backdrop-blur-md dark:border-border dark:bg-card dark:text-muted-foreground">
        <BookOpen className="mx-auto mb-2 h-6 w-6 text-[#7a6ca8]" />
        We couldn&rsquo;t load your dreams just now. Nothing has been lost — they
        are safely stored.
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 block w-full rounded-full border border-gold/40 bg-card px-4 py-2 text-sm font-medium text-foreground transition hover:bg-gold/10"
          >
            Try again
          </button>
        )}
      </div>
    );
  }
  if (dreams.length === 0) {
    return (
      <div className="rounded-2xl border border-white/70 bg-white/60 p-5 text-center text-sm text-[#5a5775] backdrop-blur-md dark:border-border dark:bg-card dark:text-muted-foreground">
        <BookOpen className="mx-auto mb-2 h-6 w-6 text-[#7a6ca8]" />
        Your dreams will appear here once you record your first one.
      </div>
    );
  }

  const shown = limit ? dreams.slice(0, limit) : dreams;

  if (carousel) {
    return (
      <SwipeRail label="Recent dreams">
        {shown.map((d) => {
          const kind = dreamKind(d);
          const themes = [...d.tags.themes, ...d.tags.symbols].slice(0, 2);
          return (
            <article
              key={d.id}
              className="flex w-[80%] shrink-0 snap-start flex-col rounded-2xl border border-gold/25 bg-card/80 p-4 shadow-sm backdrop-blur sm:w-[52%] md:w-[40%]"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                  <span aria-hidden>{kind.emoji}</span> {kind.label}
                </span>
                <div className="flex shrink-0 items-center gap-1">
                  <FavoriteButton
                    storageKey="rt:fav-dream-entries"
                    id={d.id}
                    label={d.title?.trim() || "Untitled dream"}
                    sub="Dream journal entry"
                    to="/dreams"
                    size="sm"
                  />
                  <button
                    onClick={() => {
                      if (confirm("Delete this dream? This can't be undone.")) onDelete(d.id);
                    }}
                    aria-label="Delete dream"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground/35 transition hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="mt-0.5 text-[11px] text-foreground/55">{relativeDate(d.created_at)}</p>
              <p className="mt-2 line-clamp-2 font-display text-sm text-foreground">
                {d.title?.trim() || "Untitled dream"}
              </p>
              {d.mood && (
                <p className="mt-1.5 text-xs text-foreground/70">Feeling: {d.mood}</p>
              )}
              {themes.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {themes.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-gold/25 bg-gold/10 px-2 py-0.5 text-[11px] capitalize text-foreground/75"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => onOpen(d)}
                className="mt-3 inline-flex items-center gap-1 self-start text-xs font-medium text-[var(--gold-deep)] hover:underline"
              >
                Open dream →
              </button>
            </article>
          );
        })}
      </SwipeRail>
    );
  }

  return (

    <ul className="divide-y divide-white/60 overflow-hidden rounded-2xl border border-white/70 bg-white/70 shadow-sm backdrop-blur-md dark:divide-border dark:border-border dark:bg-card">
      {shown.map((d) => (
        <li key={d.id} className="group relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-1">
          <button
            onClick={() => onOpen(d)}
            className="flex flex-1 items-center gap-3 px-3 py-2.5 text-start transition hover:bg-white/60 dark:hover:bg-accent md:px-4 md:py-3"
          >
            <span className="text-xl" aria-hidden>
              🌙
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-display text-sm text-[#2c2a4a] dark:text-foreground md:text-base">
                {d.title?.trim() || "Untitled dream"}
              </span>
              <span className="block truncate text-[11px] text-[#68657d] dark:text-muted-foreground md:text-xs">
                {relativeDate(d.created_at)}
                {d.mood ? ` · ${d.mood}` : ""}
              </span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-[#9c98b3]" />
          </button>
          <div className="me-2 flex shrink-0 items-center gap-1">
            <FavoriteButton
              storageKey="rt:fav-dream-entries"
              id={d.id}
              label={d.title?.trim() || "Untitled dream"}
              sub="Dream journal entry"
              to="/dreams"
              size="sm"
            />
            <button
              onClick={() => {
                if (confirm("Delete this dream? This can't be undone.")) {
                  onDelete(d.id);
                }
              }}
              aria-label="Delete dream"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#9c98b3] transition hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ───────────────── Dream detail view ───────────────── */
const DEFAULT_REFLECTION_QUESTIONS = [
  "What emotions stood out?",
  "Did anything feel familiar?",
  "What part of the dream stays with you?",
  "What feels important about it right now?",
];

function DreamDetailView({
  dream,
  onBack,
  onReflectionSaved,
}: {
  dream: DreamRow;
  onBack: () => void;
  onReflectionSaved: (updated: DreamRow) => void;
}) {
  const update = useServerFn(updateDreamReflection);
  const allTags = useMemo(() => {
    const t = dream.tags;
    return [
      ...t.symbols.map((x) => ({ kind: "symbol", label: x })),
      ...t.emotions.map((x) => ({ kind: "emotion", label: x })),
      ...t.places.map((x) => ({ kind: "place", label: x })),
      ...t.people.map((x) => ({ kind: "person", label: x })),
      ...t.themes.map((x) => ({ kind: "theme", label: x })),
    ];
  }, [dream.tags]);

  const existingQuestions = dream.reflection.map((r) => r.question);
  const initialQuestions =
    existingQuestions.length > 0 ? existingQuestions : DEFAULT_REFLECTION_QUESTIONS;
  const [editing, setEditing] = useState(dream.reflection.length === 0);
  const [answers, setAnswers] = useState<string[]>(() =>
    initialQuestions.map(
      (q) => dream.reflection.find((r) => r.question === q)?.answer ?? "",
    ),
  );
  const [saving, setSaving] = useState(false);

  async function onSaveReflection() {
    if (saving) return;
    setSaving(true);
    try {
      const reflection = initialQuestions
        .map((q, i) => ({ question: q, answer: answers[i]?.trim() ?? "" }))
        .filter((r) => r.answer.length > 0);
      if (dream.id.startsWith("guest-")) {
        toast("Sign in to save reflections to your journal.");
        setSaving(false);
        return;
      }
      toast.loading("Saving reflection…", { id: "dream-detail-save" });
      await ensureFreshSession();
      await update({ data: { id: dream.id, reflection } });
      toast.success("Reflection saved", { id: "dream-detail-save" });
      onReflectionSaved({ ...dream, reflection });
      setEditing(false);
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : "";
      toast.error(raw || "Couldn't save reflection — please try again.", {
        id: "dream-detail-save",
        description: raw ? "Exact server response shown above." : undefined,
        duration: 8000,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <button
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-[#5a5775] hover:text-[#2c2a4a] dark:text-muted-foreground dark:hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Back to dreams
      </button>

      <article className="rounded-2xl border border-white/70 bg-white/70 p-6 shadow-sm backdrop-blur-md dark:border-border dark:bg-card">
        <h2 className="font-display text-2xl text-[#2c2a4a] dark:text-foreground">
          {dream.title?.trim() || "Untitled dream"}
        </h2>
        <p className="mt-1 text-xs text-[#68657d] dark:text-muted-foreground">
          {new Date(dream.created_at).toLocaleString()}
          {dream.mood ? ` · Mood: ${dream.mood}` : ""}
        </p>
        <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-[#2c2a4a] dark:text-foreground/90">
          {dream.body}
        </p>

        {allTags.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-1.5">
            {allTags.map((t, i) => (
              <span
                key={i}
                className="rounded-full border border-[#e6dff2] bg-white/70 px-2.5 py-0.5 text-[11px] font-medium text-[#5a4b7a] dark:border-border dark:bg-background dark:text-muted-foreground"
              >
                {t.label}
              </span>
            ))}
          </div>
        )}
      </article>

      {/* Saved reflections (read-only view) */}
      {!editing && dream.reflection.length > 0 && (
        <section className="mt-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base text-[#2c2a4a] dark:text-foreground">🤍 Your reflection</h3>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-full border border-[#dcd6ea] bg-white/80 px-3 py-1 text-xs font-medium text-[#5a4b7a] hover:text-[#2c2a4a] dark:border-border dark:bg-background dark:text-muted-foreground dark:hover:text-foreground"
            >
              Edit
            </button>
          </div>
          {dream.reflection.map((r, i) => (
            <div
              key={i}
              className="rounded-2xl border border-white/70 bg-white/60 p-5 backdrop-blur-md dark:border-border dark:bg-card"
            >
              <p className="text-xs font-medium text-[#7a6ca8]">{r.question}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-[#2c2a4a] dark:text-foreground/90">{r.answer}</p>
            </div>
          ))}
        </section>
      )}

      {/* Reflection editor */}
      {editing && (
        <section className="mt-5">
          <div className="mb-3 rounded-2xl border border-white/70 bg-white/60 p-5 backdrop-blur-md dark:border-border dark:bg-card">
            <h3 className="font-display text-base text-[#2c2a4a] dark:text-foreground">🤍 Gentle reflection</h3>
            <p className="mt-1 text-sm text-[#5a5775] dark:text-muted-foreground">
              Take what resonates. Skip what doesn't. There's no right answer.
            </p>
          </div>
          <div className="space-y-3">
            {initialQuestions.map((q, i) => (
              <div
                key={i}
                className="rounded-2xl border border-white/70 bg-white/70 p-5 backdrop-blur-md dark:border-border dark:bg-card"
              >
                <label className="block text-sm font-medium text-[#2c2a4a] dark:text-foreground">{q}</label>
                <textarea
                  rows={3}
                  value={answers[i] ?? ""}
                  onChange={(e) => {
                    const next = [...answers];
                    next[i] = e.target.value;
                    setAnswers(next);
                  }}
                  className="mt-2 w-full resize-y rounded-xl border border-[#dcd6ea] bg-white/80 p-3 text-sm text-[#2c2a4a] placeholder:text-[#777189] focus:outline-none focus:ring-2 focus:ring-[#b9a8d9] dark:border-border dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground dark:focus:ring-gold"
                  maxLength={2000}
                  placeholder="A few words is enough…"
                />
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-col-reverse items-stretch justify-end gap-3 sm:flex-row sm:items-center">
            {dream.reflection.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setAnswers(
                    initialQuestions.map(
                      (q) => dream.reflection.find((r) => r.question === q)?.answer ?? "",
                    ),
                  );
                }}
                className="rounded-full border border-[#dcd6ea] bg-white/80 px-5 py-2.5 text-sm font-medium text-[#5a5775] hover:text-[#2c2a4a] dark:border-border dark:bg-background dark:text-muted-foreground dark:hover:text-foreground"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={(ev) => {
                if (saving) return;
                ev.preventDefault();
                if (
                  typeof document !== "undefined" &&
                  document.activeElement instanceof HTMLElement
                ) {
                  document.activeElement.blur();
                }
                void onSaveReflection();
              }}
              disabled={saving}
              className="relative z-20 inline-flex min-h-[48px] touch-manipulation items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#7a6ca8] to-[#a88dc4] px-6 py-3 text-sm font-medium text-white shadow-md transition hover:opacity-90 disabled:opacity-60 sm:min-h-0 sm:py-2.5"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                </>
              ) : (
                "Save reflection"
              )}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

/* ───────────────── Patterns tab ───────────────── */
function PatternsInsights({ patterns }: { patterns: DreamPatterns | null }) {
  if (patterns === null) {
    return (
      <div className="rounded-2xl border border-white/70 bg-white/60 p-8 text-center text-sm text-[#5a5775] backdrop-blur-md dark:border-border dark:bg-card dark:text-muted-foreground">
        <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-[#7a6ca8]" />
        Looking for patterns in your dreams…
      </div>
    );
  }
  if (patterns.totals.dreams < 2) {
    return (
      <div className="rounded-2xl border border-white/70 bg-white/60 p-8 text-center text-sm text-[#5a5775] backdrop-blur-md dark:border-border dark:bg-card dark:text-muted-foreground">
        <TrendingUp className="mx-auto mb-2 h-6 w-6 text-[#7a6ca8]" />
        Patterns will appear after you've recorded a few dreams. The more you record, the more
        you'll notice what gently returns.
      </div>
    );
  }

  const groups: { title: string; emoji: string; buckets: typeof patterns.symbols }[] = [
    { title: "Recurring themes", emoji: "💭", buckets: patterns.themes },
    { title: "Emotions", emoji: "🤍", buckets: patterns.emotions },
    { title: "Symbols", emoji: "🔮", buckets: patterns.symbols },
    { title: "People", emoji: "👤", buckets: patterns.people },
    { title: "Places", emoji: "📍", buckets: patterns.places },
  ].filter((g) => g.buckets.length > 0);

  if (groups.length === 0 && patterns.insights.length === 0) return null;

  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-2 gap-2.5">
        {groups.map((g) => (
          <div
            key={g.title}
            className="rounded-2xl border border-gold/25 bg-card/80 p-3.5 shadow-sm backdrop-blur"
          >
            <h3 className="mb-2 flex items-center gap-1.5 font-display text-sm text-foreground">
              <span aria-hidden>{g.emoji}</span>
              <span className="truncate">{g.title}</span>
            </h3>
            <ul className="space-y-1 text-xs">
              {g.buckets.slice(0, 3).map((b) => (
                <li key={b.label} className="flex items-baseline justify-between gap-2">
                  <span className="truncate capitalize text-foreground/85">{b.label}</span>
                  <span className="shrink-0 text-[11px] text-foreground/55">· {b.count}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {patterns.insights.length > 0 && (
        <div className="rounded-2xl border border-gold/25 bg-gold/5 p-3.5 backdrop-blur">
          <ul className="space-y-1 text-xs leading-relaxed text-foreground/80 md:text-sm">
            {patterns.insights.map((i, idx) => (
              <li key={idx}>{i}</li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] italic text-foreground/55">
            Simple counts of what you've written — not an interpretation. What they mean is yours to decide.
          </p>
        </div>
      )}
    </div>
  );
}


function SymbolExplorer({
  onSit,
  autoFocusSearch = false,
}: {
  onSit: (symbol: string) => void;
  autoFocusSearch?: boolean;
}) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return DREAM_SYMBOLS;
    return DREAM_SYMBOLS.filter((s) => {
      const hay = [s.symbol, ...(s.keywords ?? []), ...s.themes]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [q]);

  useEffect(() => {
    if (autoFocusSearch && inputRef.current) {
      // small delay so the section is mounted/visible before focusing
      const t = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
  }, [autoFocusSearch]);

  return (
    <div>
      <div className="mb-3 px-1">
        <h2 className="font-display text-lg text-foreground md:text-xl">
          🔮 Symbol Explorer
        </h2>
        <p className="mt-1 text-xs text-foreground/70 md:text-sm">
          A few common dream images, held lightly. Your own meaning matters most.
        </p>
        <p className="mt-1 text-[11px] italic text-foreground/55 md:text-xs">
          Dream symbols do not have one fixed meaning. This section is for gentle reflection only — not interpretation, diagnosis, or therapy.
        </p>
      </div>

      <div className="mb-3 px-1">
        <label htmlFor="symbol-search" className="sr-only">
          Search dream symbols
        </label>
        <input
          ref={inputRef}
          id="symbol-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search dream symbols…"
          className="w-full rounded-2xl border border-sage/30 bg-card/80 px-4 py-2.5 text-sm text-foreground placeholder:text-foreground/40 shadow-sm backdrop-blur focus:border-[var(--gold-deep)] focus:outline-none focus:ring-2 focus:ring-gold/30"
        />
        <p className="mt-1.5 px-1 text-[11px] text-foreground/50">
          Try: water, house, door, child, animal, falling, being chased, teeth, death, mother, father
        </p>
      </div>


      {filtered.length === 0 ? (
        <p className="rounded-2xl border border-sage/30 bg-card/80 px-4 py-6 text-center text-sm text-foreground/70 shadow-sm backdrop-blur">
          No matching symbol yet — your own meaning matters most. Try a related word, or sit with the image as it is.
        </p>
      ) : (
        <div className="grid gap-2.5 md:grid-cols-2 md:gap-3">
          {filtered.map((s) => (
            <article
              key={s.symbol}
              className="rounded-2xl border border-sage/30 bg-card/80 p-4 shadow-sm backdrop-blur"
            >
              <div className="flex items-center gap-2">
                <span className="text-xl" aria-hidden>
                  {s.emoji}
                </span>
                <h3 className="font-display text-base text-foreground">{s.symbol}</h3>
              </div>

              <p className="mt-2 text-sm leading-relaxed text-foreground/80">
                {s.reflection}
              </p>

              {s.themes.length > 0 && (
                <div className="mt-3">
                  <p className="text-[11px] uppercase tracking-wide text-foreground/55">
                    Possible emotional themes
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {s.themes.map((t) => (
                      <span
                        key={t}
                        className="rounded-full border border-sage/30 bg-sage/10 px-2.5 py-0.5 text-[11px] text-foreground/75"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-3 rounded-xl border border-gold/25 bg-gold/5 p-3">
                <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--gold-deep)]">
                  Gentle reflection
                </p>
                <ul className="mt-1.5 space-y-1 text-xs text-foreground/80 md:text-sm">
                  {s.questions.map((question) => (
                    <li key={question} className="flex gap-2">
                      <span aria-hidden className="text-[var(--gold-deep)]">•</span>
                      <span>{question}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="mt-3 text-[11px] italic text-foreground/55">
                Only take what feels right. You can stop at any time.
              </p>

              <button
                type="button"
                onClick={() => onSit(s.symbol)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--gold-deep)] hover:underline"
              >
                Sit with this symbol →
              </button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function LensCard({
  lens,
}: {
  lens: { title: string; emoji: string; body: string; more: string[] };
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <article
      className="flex w-[82%] shrink-0 snap-start flex-col rounded-2xl border border-gold/25 bg-card/80 p-4 shadow-sm backdrop-blur sm:w-[60%] md:w-[46%]"
    >
      <div className="flex items-center gap-2">
        <span className="text-xl" aria-hidden>{lens.emoji}</span>
        <h3 className="font-display text-base text-foreground">{lens.title}</h3>
      </div>
      <p className="mt-1.5 text-sm leading-relaxed text-foreground/80">{lens.body}</p>

      {expanded && (
        <div className="mt-3 space-y-2.5 border-t border-gold/20 pt-3">
          {lens.more.map((para, i) => (
            <p key={i} className="text-sm leading-relaxed text-foreground/80">
              {para}
            </p>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 self-start text-xs font-medium text-[var(--gold-deep)] hover:underline"
        aria-expanded={expanded}
      >
        {expanded ? "Show less" : "Explore →"}
      </button>
    </article>
  );
}

